import SkuCardsResponsive from '@/components/common/cards/sku/SkuCardsResponsive';
import DateComponent from '@/components/common/DateComponent';
import Container from '@/components/Container';
import useWindowWidth from '@/hooks/useWindowWidth';
import getCollectionSlugGetServerSideProps from '@/lib/serverSideProps/serverSidePropsPages/getCollectionSlugGetServerSideProps';
import websiteIcon from '@/public/images/collections/globe.svg';
import instaIcon from '@/public/images/collections/instagram.svg';
import collectionBg from '@/public/images/collections/single-collection-bg.png';
import { SkuDTO } from '@/types/api/sku';
import { SingleCollectionProps } from '@/types/props/pages';
import translationFallback from '@/utilites/translations/translationFallback';
import { useTranslation } from 'next-i18next';
import Head from 'next/head';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import styles from './collectionId.module.scss';
import { SingleCollection as SingleCollectionType } from '../../types/other';
import useBackendUser from '@/hooks/useBackendUser';

const loaderProp = ({ src }: { src: string }) => {
  return src;
};

const SingleCollection = ({
  collection,
  windowWidthSv = 0,
  user: userSSR = null,
}: SingleCollectionProps) => {
  const router = useRouter();
  const { data: user } = useBackendUser();
  const windowWidth = useWindowWidth(windowWidthSv);

  const { t: tCommon } = useTranslation('common');

  // Safely destructure collection attributes with default values
  const {
    name = '',
    description = '',
    createdAt = '',
    locale = '',
    contributor = { data: { attributes: {} } },
    image = { data: { attributes: {} } },
    skus = { data: [] },
    localizations = { data: [] },
  } = (collection?.attributes || {}) as SingleCollectionType['attributes'];

  const collectionBackground =
    collection?.attributes?.image?.data?.attributes?.url || collectionBg.src;

  const {
    name: contributorName = '',
    image: contributorImageData = { data: { attributes: { url: '' } } },
    instagram: contributorInstagram = null,
    website: contributorWebsite = null,
    short_description: contributorDescription = '',
  } = (contributor?.data?.attributes || {
    name: '',
    image: { data: { attributes: { url: '' } } },
    instagram: null,
    website: null,
    short_description: '',
  }) as SingleCollectionType['attributes']['contributor']['data']['attributes'];

  const contributorImage = contributorImageData?.data?.attributes?.url || '';
  const collectionAvatar = contributorImage || 'https://placehold.co/141';

  const skusData = skus?.data || [];
  const localizationsData = localizations?.data || [];

  const socialMediaAccounts = {
    instagram: contributorInstagram,
    website: contributorWebsite,
  };

  useEffect(() => {
    const findGivenCollection =
      localizationsData.find(
        localization =>
          (localization?.attributes?.locale || '').toLowerCase() ===
          (router.locale || '').toLowerCase()
      )?.attributes?.slug || null;
    if (findGivenCollection && locale !== router.locale) {
      router.replace(`/blog/${findGivenCollection}`, undefined, {
        shallow: false,
        locale: router.locale,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.locale, localizationsData, locale]);

  return (
    <>
      <Head>
        <title>
          Discover {name} by {contributorName}
        </title>
        <meta name={`title`} content={`Discover ${name} by ${contributorName}`} />

        <meta
          name="description"
          content={`Discover ${name} the new Spiritory Collection by Whisky-Enthusiast ${contributorName}`}
        />

        <meta
          name={'keywords'}
          content={`spiritory, marketplace, buy, sell, ${name}, ardbeg, rare whiskey, japanese, irish, bourbon`}
        />

        <link rel="canonical" href={`https://spiritory.com${router.asPath}`} />
      </Head>
      <div className={`position-relative py-5 ${styles.textContainer}`}>
        <Image
          src={collectionBg}
          width={0}
          height={0}
          className={`w-100 h-100 object-fit-cover position-absolute start-0 top-0 end-0 bottom-0 z-index-n1 ${styles.backgroundImage}`}
          alt="Collections"
        />
        <Container
          className={`py-5 rounded-5 my-5 tw-bg-white !tw-bg-opacity-85 ${styles.mainContainer}`}
        >
          <Row className="align-items-center">
            <Col className={`d-flex align-items-center`}>
              <div className={`ms-5 ${styles.mainTitle}`}>
                <h1 className="h1 border-bottom border-gold border-5 d-table font-size-36 mb-4">
                  {translationFallback('collection_by', tCommon('collection_by'), 'Collection by')}{' '}
                  {contributorName}
                </h1>
                <h2 className="s-title fw-semibold tw-pb-2">
                  {name}{' '}
                  <span className="font-size-18 fw-normal ms-3">
                    {skusData.length || 0} {translationFallback('items', tCommon('items'), 'items')}
                  </span>
                </h2>
                <p className="font-size-18 tw-line-height-[1.75] tw-font-light">{description}</p>
              </div>
            </Col>
            <Col lg={3} className="text-center">
              <div className="d-flex flex-column align-items-center">
                <div className="d-flex flex-column align-items-center gap-2">
                  <Image
                    src={collectionAvatar}
                    alt="avatar"
                    className="rounded-circle border border-white object-fit-cover"
                    width={141}
                    height={141}
                    loader={loaderProp}
                  />
                  <h2 className={`tw-text-[16px] tw-font-normal`}>{contributorName}</h2>
                </div>
                <div className="d-flex gap-4 justify-items-center">
                  {socialMediaAccounts.instagram && (
                    <Col xs="auto">
                      <a href={socialMediaAccounts.instagram} target="_blank" rel="noreferrer">
                        <Image
                          width={35}
                          height={35}
                          src={instaIcon}
                          alt={'instagram of ' + contributorName}
                        />
                      </a>
                    </Col>
                  )}
                  {socialMediaAccounts.website && (
                    <Col xs="auto">
                      <a href={socialMediaAccounts.website} target="_blank" rel="noreferrer">
                        <Image
                          width={35}
                          height={35}
                          src={websiteIcon}
                          alt={'website of ' + contributorName}
                        />
                      </a>
                    </Col>
                  )}
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
      <div
        style={{
          paddingBottom: '1rem',
        }}
        className={styles.hideDesktop}
      >
        <div
          className="position-relative"
          style={{
            height: 260,
          }}
        >
          <div className={styles.contributorImage}>
            <Image
              src={contributorImage || ''}
              alt={'photo of ' + contributorName}
              width={1000}
              height={1000}
            />
          </div>
          <Image
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
            alt="collection-background"
            width={1000}
            height={1000}
            src={collectionBackground}
          />
        </div>
        <div
          style={{
            paddingTop: '4.8rem',
            paddingLeft: '0.8rem',
            paddingRight: '0.8rem',
            fontSize: 13,
          }}
        >
          <div className="d-flex flex-column" style={{ marginBottom: '0.5rem' }}>
            <span style={{ fontSize: 16, marginBottom: '0.5rem' }}>
              <b>{contributorName}</b>
            </span>
            <span>
              {translationFallback('by', tCommon('by'), 'by')} <b>{contributorName}</b>
            </span>
          </div>
          <div className="d-flex align-items-center gap-1" style={{ marginBottom: '0.5rem' }}>
            <span>
              {translationFallback('items', tCommon('items'), 'items')}{' '}
              <b>{skusData.length || 0}</b>
            </span>
            •
            <span>
              {translationFallback('created', tCommon('created'), 'created')}{' '}
              <b>
                <DateComponent date={createdAt} />
              </b>
            </span>
          </div>
          <p style={{ marginBottom: 0, paddingBottom: 0, fontSize: 13 }}>
            {contributorDescription}
          </p>
        </div>
      </div>
      <Container className="pt-4">
        <ul className="!tw-m-0 tw-grid tw-w-full tw-grid-cols-3 tw-gap-4 !tw-px-0 tw-pb-8 max-md:tw-grid-cols-2 max-sm:tw-grid-cols-1">
          <SkuCardsResponsive
            skus={skusData.map(attr => attr?.attributes || {}) as unknown as SkuDTO[]}
            windowWidth={windowWidth}
            userId={user?.id ?? null}
          />
        </ul>
      </Container>
    </>
  );
};

export const getServerSideProps = getCollectionSlugGetServerSideProps;

export default SingleCollection;
